<script setup lang="ts">
useHead({
  title: "Zenapptic AI - Digital Workplace",
});
</script>

<template>
  <SolutionsLayout>
    <SolutionsDetailsTitle
      :background-url="`${useCdnUrl()}/assets/solutions/digital-workplace/digital-workplace-placeholder.webp`"
    >
      <template #videos>
        <source
          :src="`${useCdnUrl()}/assets/solutions/digital-workplace/digital-workplace-background.webm`"
          type="video/webm"
        />
      </template>

      <template #title><b>DIGITAL</b><br />WORKPLACE</template>

      <template #description>
        Work is now an experience too.<br />
        Foster collaboration, engagement, and efficiency with a connected workforce and workplace,
        all powered by <b>ZEN3</b>.
      </template>
    </SolutionsDetailsTitle>

    <div class="feature-content">
      <SolutionsCalloutBlock
        :background-url="`${useCdnUrl()}/assets/solutions/digital-workplace/platform-video-background.webp`"
      >
        <template #details-left>
          <h2>The <b>ZEN3</b> Platform Powers<br />the Digital Workplace</h2>

          <p>
            <b>ZEN3</b> manages the end-to-end process, creating real-time connections between
            users, content, and devices
          </p>
          <p>
            ... and it's pivotal in building virtual communities that connect users through
            interactive visual experiences.
          </p>
        </template>

        <template #details-right>
          <VideoPlayer
            :video-url="`${useCdnUrl()}/assets/solutions/digital-workplace/digital-workplace-explainer.webm`"
            ><img
              :src="`${useCdnUrl()}/assets/solutions/digital-workplace/digital-workplace-explainer-placeholder.webp`"
              alt="Digital workplace explainer video"
          /></VideoPlayer>
        </template>
      </SolutionsCalloutBlock>

      <SolutionsDetailsBlock>
        <template #image>
          <img
            :src="`${useCdnUrl()}/assets/solutions/digital-workplace/command-center.webp`"
            alt="Command Centers"
          />
        </template>

        <template #title>The New <br />Command Center</template>
        <template #description
          ><p>
            Ideal for emergency services, security, transportation, supply chains, and corporate
            operations, the ZEN3 platform extends the typical capabilities of Command Center
            software and further empowers decision-makers
          </p>
        </template>

        <template #items>
          <ul>
            <li>
              <b>Any Data Integration:</b> Integrate data from multiple sources, including sensors,
              IoT devices, surveillance cameras, real-time video streams, social media, smartphones,
              and more to provide comprehensive situational awareness
            </li>
            <li>
              <b>Collaboration Features & Mobile Accessibility:</b> Facilitate communication and
              coordination among team members on site, between sites, and with remote team members
            </li>
            <li>
              <b>VR Command Center:</b> Make all the same data, dashboards, and contextualized
              information available via virtual reality headsets
            </li>
          </ul>
        </template>
      </SolutionsDetailsBlock>

      <SolutionsCalloutBlock
        :background-url="`${useCdnUrl()}/assets/solutions/digital-workplace/virtual-hub-background.webp`"
      >
        <template #details-left>
          <h2>Ready to enhance your<br />Command Center with a<br />virtual hub?</h2>

          <p>Check out our concept video</p>
        </template>

        <template #details-right>
          <VideoPlayer
            :video-url="`${useCdnUrl()}/assets/solutions/digital-workplace/command-center.webm`"
            ><img
              :src="`${useCdnUrl()}/assets/solutions/digital-workplace/command-center-placeholder.webp`"
              alt="Command Center Demo"
          /></VideoPlayer>
        </template>
      </SolutionsCalloutBlock>

      <SolutionsDetailsBlock>
        <template #image>
          <img
            :src="`${useCdnUrl()}/assets/solutions/digital-workplace/smart-factory.webp`"
            alt="Smart Factory"
          />
        </template>

        <template #title>SMART FACTORIES</template>
        <template #description
          ><p>
            Unlock unparalleled efficiency and innovation with the ZEN3 platform for Smart Factories
            featuring:
          </p>

          <ul>
            <li>Visual representations of the manufacturing process</li>
            <li>Real-time monitoring from unlimited data sources and IoT sensors</li>
            <li>Predictive analysis</li>
            <li>
              Integration with third-party systems (ERP, Supply Chain Management, QMS, HR,
              Cybersecurity, and more)
            </li>
          </ul></template
        >
      </SolutionsDetailsBlock>

      <SolutionsCalloutBlock
        :background-url="`${useCdnUrl()}/assets/solutions/digital-workplace/smart-factory-callout.webp`"
      >
        <template #details-left>
          <h2>
            See how <b>ZEN3</b> provides<br /><b>real-time</b> insights, yielding<br />greater
            opertional efficiences
          </h2>
        </template>

        <template #details-right>
          <VideoPlayer
            :video-url="`${useCdnUrl()}/assets/solutions/digital-workplace/smart-factory-video.webm`"
            ><img
              :src="`${useCdnUrl()}/assets/solutions/digital-workplace/smart-factory-placeholder.webp`"
              alt="Smart Factory"
          /></VideoPlayer>
        </template>
      </SolutionsCalloutBlock>

      <SolutionsDetailsBlock>
        <template #image>
          <img
            :src="`${useCdnUrl()}/assets/solutions/digital-workplace/digital-twins.webp`"
            alt="Digital Twins"
          />
        </template>

        <template #title>DIGITAL TWINS</template>
        <template #description
          ><p>
            Digital Twins offer unprecedented insights, efficiency gains, and opportunities for
            continuous improvement
          </p>
        </template>
      </SolutionsDetailsBlock>

      <SolutionsCalloutBlock
        :background-url="`${useCdnUrl()}/assets/solutions/digital-workplace/digital-twin-callout-background.webp`"
        class="digital-twin-override"
      >
        <template #details-left>
          <h2>
            <b>"Digital twin</b> systems allow<br />data-driven decision-making<br />
            all along the lifecycle and<br />
            across ecosystems
          </h2>
          <p>
            Capgemini Research Institute,<br /><a
              href="https://www.capgemini.com/gb-en/wp-content/uploads/sites/5/2022/11/DigitalTwins_adding-intelligence-to-the-real-world.pdf"
              target="_blank"
              style="font-size: 1.5rem"
              >Digital Twins: Adding Intelligence to the Real World</a
            >
          </p>
        </template>

        <template #details-right>
          <div class="digital-twins-chart">
            <h3>Most impactful services offered by a digital twin:</h3>
            <img
              :src="`${useCdnUrl()}/assets/solutions/digital-workplace/digital-twin-chart.png`"
            />
          </div>
        </template>
      </SolutionsCalloutBlock>

      <SolutionsDetailsBlock>
        <template #image>
          <img
            :src="`${useCdnUrl()}/assets/solutions/digital-workplace/realtime-dashboard.webp`"
            alt="Realtime Data Dashboards"
          />
        </template>

        <template #title>REAL-TIME<br />DATA DASHBOARDS</template>
        <template #description
          ><p>
            Whether in Augmented Reality (AR), Virtual Reality (VR), or on digital display screens,
            the ZEN3 platform provides dynamic and interactive displays of information, enabling
            users to gain insights, make more informed decisions, and respond quickly as issues
            arise
          </p>

          <p><b>ZEN3</b> uses AI to provide even more capabilities for better decision-making:</p>

          <ul>
            <li>
              <b>Automated Insights:</b> Analyze real-time data streams and automatically identify
              patterns, trends, and anomalies without manual intervention
            </li>

            <li>
              <b>Predictive Analytics:</b> AI-powered algorithms predict future trends based on
              historical and current data, anticipating potential issues or opportunities
            </li>
          </ul>
        </template>
      </SolutionsDetailsBlock>
    </div>

    <ContactBlock />
  </SolutionsLayout>
</template>

<style lang="scss">
.digital-twin-override {
  .container {
    > * {
      flex: 50%;
    }
    .callout-details {
      > div {
        border: none;
        padding-left: 0;
      }

      &:before {
        background-color: #6734bb;
        content: "";
        height: 0px;
        left: 0;
        position: absolute;
        top: 0;
        width: 0px;
      }
    }
  }
  .digital-twins-chart {
    display: flex;
    flex-direction: column;

    h3 {
      font-size: 2rem;
    }

    img {
      width: 100%;
    }
  }
}
</style>
